import { svgIconPaths } from '@/assets/svg/icons';
import Dropdown from '@/components/atoms/Dropdown';
import SvgIcon from '@/components/atoms/SvgIcon';
import colors from '@/theme/colors';
import { CREATOR_APPLICANT_TYPES, TCreatorApplicantType } from '@/types/CreatorApplication.types';
import { CloseIcon } from '@chakra-ui/icons';
import {
  Button,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Spinner,
} from '@chakra-ui/react';
import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';

const SEARCH_DEBOUNCE_MS = 400;
const APPLICANT_TYPE_ALL = 'All';
const APPLICANT_TYPE_OPTIONS = [APPLICANT_TYPE_ALL, ...CREATOR_APPLICANT_TYPES] as const;

interface Props {
  searchText: string;
  applicantType?: TCreatorApplicantType;
  isSearching: boolean;
  onSearchChange: (value: string) => void;
  onApplicantTypeChange: (value?: TCreatorApplicantType) => void;
}

const CreatorApplicationsFilters = ({
  searchText,
  applicantType,
  isSearching,
  onSearchChange,
  onApplicantTypeChange,
}: Props) => {
  // Local copy so the input stays responsive while the URL-bound value updates on a debounce.
  const [localSearchText, setLocalSearchText] = useState(searchText);

  // Keep in sync when the value changes externally (browser back/forward, clear, deep link).
  useEffect(() => {
    setLocalSearchText(searchText);
  }, [searchText]);

  // Stable debounced emitter that always calls the latest handler.
  const onSearchChangeRef = useRef(onSearchChange);
  onSearchChangeRef.current = onSearchChange;
  const debouncedEmit = useMemo(
    () => debounce((value: string) => onSearchChangeRef.current(value), SEARCH_DEBOUNCE_MS),
    [],
  );
  useEffect(() => () => debouncedEmit.cancel(), [debouncedEmit]);

  const handleSearchChange = (value: string) => {
    setLocalSearchText(value);
    debouncedEmit(value);
  };

  const clearSearch = () => {
    debouncedEmit.cancel();
    setLocalSearchText('');
    onSearchChange('');
  };

  return (
    <HStack gap={'15px'}>
      <Dropdown
        title="Applicant type"
        defaultOption={APPLICANT_TYPE_ALL}
        selectedOption={applicantType ?? APPLICANT_TYPE_ALL}
        options={APPLICANT_TYPE_OPTIONS}
        formatValues
        onOptionClick={(option) =>
          onApplicantTypeChange(
            option === APPLICANT_TYPE_ALL ? undefined : (option as TCreatorApplicantType),
          )
        }
      />
      <InputGroup width={'300px'}>
        <InputLeftElement pointerEvents={'none'} height={'100%'}>
          <SvgIcon iconPath={svgIconPaths.magnifyingGlass} color={colors.text.mediumGray} />
        </InputLeftElement>
        <Input
          backgroundColor={'white'}
          placeholder={'Search by name or email'}
          _placeholder={{ textOverflow: 'ellipsis', overflow: 'hidden' }}
          value={localSearchText}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
        {(isSearching || localSearchText.length > 0) && (
          <InputRightElement width={'48px'} height={'100%'}>
            {isSearching ? (
              <Spinner size={'sm'} color={colors.text.mediumGray} />
            ) : (
              <Button height={'30px'} width={'30px'} minWidth={'30px'} onClick={clearSearch}>
                <CloseIcon color={colors.text.mediumGray} boxSize={3} />
              </Button>
            )}
          </InputRightElement>
        )}
      </InputGroup>
    </HStack>
  );
};

export default CreatorApplicationsFilters;
